import { useEffect, useState } from 'react';

import useDisableScroll from '@/hooks/useDisableScroll';

import { useFormDataQuery } from '@/api/caseCard/getters';
import {
  CaseCardDetailsPayload,
  CaseCardDetailsValidationError,
  useUpdateCaseCardDetailsMutation,
} from '@/api/caseCard/setters';
import IconInfo from '@/assets/info-shape.svg?react';
import Pencil from '@/assets/pencil.svg?react';
import ChatMessage from '@/components/ChatMessage';
import Button, { ButtonLoading } from '@/components/ui/Button';
import ErrorMessage from '@/components/ui/ErrorMessage';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/Form';
import { Input } from '@/components/ui/Input';
import { InputMask } from '@/components/ui/InputMask';
import { CaseCardPageData } from '@/features/caseCard/content.types';
import InputCity from '@/features/v2/summaryForms/components/InputCity';
import { validateNewEmail } from '@/features/v2/summaryForms/Validate';
import { EnumLike } from '@/types';

import { useLingui } from '@lingui/react';
import { useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { FieldPath, useForm } from 'react-hook-form';

interface DetailsFormValues {
  email: string;
  plot: {
    zip_code: string;
    city: EnumLike | '';
    address: string;
    title_register_number: string;
  };
}

// Same format the backend enforces (TitleRegisterNumber value object).
const TITLE_REGISTER_NUMBER_PATTERN = /^[A-Z]{2}\d[A-Z]\/\d{8}\/\d$/;
const ZIP_CODE_PATTERN = /^\d{2}-\d{3}$/;

// City options read "county > commune > city"; the field shows the city only.
const cityName = (label: string) => label.split(' > ').pop() ?? label;

// Backend validation keys → form fields.
const ERROR_FIELDS: Record<string, FieldPath<DetailsFormValues>> = {
  email: 'email',
  postal_code: 'plot.zip_code',
  postal_city_id: 'plot.city',
  plot_number: 'plot.address',
  title_register_number: 'plot.title_register_number',
};

interface Props {
  consultant: CaseCardPageData['chatMessage']['consultant'];
}

// FE-5372: asks the client for what the VBOT call did not collect. Opens only
// when the lead has no e-mail; the plot part is added when the lead also has
// neither a full plot address nor a KW number. Saving closes the dialog.
const DetailsDialog = ({ consultant }: Props) => {
  const { i18n } = useLingui();
  const { data } = useFormDataQuery();
  const queryClient = useQueryClient();
  const { mutateAsync: saveDetails, isPending } =
    useUpdateCaseCardDetailsMutation();
  const [focused, setFocused] = useState('');
  const methods = useForm<DetailsFormValues>({
    // An error shows after leaving the field and then follows typing, so the
    // save button (disabled while errors are shown) comes back right away.
    mode: 'onTouched',
    defaultValues: {
      email: '',
      plot: { zip_code: '', city: '', address: '', title_register_number: '' },
    },
  });

  // The city list is looked up by postal code, so the city waits for a full one
  // (same as the HH damage address).
  const zipCode = methods.watch('plot.zip_code');
  const isZipCodeComplete = ZIP_CODE_PATTERN.test(zipCode);

  // react-hook-form 7.49 drops the re-validation result of a field rendered
  // with a boolean `disabled` (the city), so an error shown after "Zapisz"
  // would stay after picking a city. Re-check the city after each change.
  const city = methods.watch('plot.city');
  const cityId = typeof city === 'object' ? city?.value : city;
  useEffect(() => {
    if (methods.getFieldState('plot.city').error) {
      void methods.trigger('plot.city');
    }
  }, [cityId, methods]);
  const hasFieldErrors = Object.keys(methods.formState.errors).some(
    (key) => key !== 'root'
  );

  const needsEmail = !!data?.is_email_empty;
  const needsPlot =
    !!data?.is_plot_address_empty && !!data?.is_title_register_number_empty;
  const isOpen = needsEmail;

  useDisableScroll(isOpen);

  if (!data || !isOpen) return null;

  const greeting = data.first_name
    ? `${i18n.t('Cześć,')} ${data.first_name}!`
    : i18n.t('Cześć!');

  const request = needsPlot
    ? i18n.t(
        'Podczas rozmowy z naszym asystentem nie udało się zapisać wszystkich informacji. Uzupełnij poniżej swój adres e-mail i dane działki — dzięki temu szybciej zajmiemy się Twoją sprawą.'
      )
    : i18n.t(
        'Podczas rozmowy z naszym asystentem nie udało się zapisać wszystkich informacji. Uzupełnij poniżej swój adres e-mail — dzięki temu szybciej zajmiemy się Twoją sprawą.'
      );

  const validateEmail = (value: string) =>
    validateNewEmail(value) === true ||
    i18n.t('Wpisz poprawny adres e-mail, np. jan.kowalski@gmail.com.');

  const submit = async ({ email, plot }: DetailsFormValues) => {
    const payload: CaseCardDetailsPayload = {
      email,
      ...(needsPlot && {
        postal_code: plot.zip_code,
        postal_city_id: Number((plot.city as EnumLike).value),
        plot_number: plot.address.trim(),
        ...(plot.title_register_number && {
          title_register_number: plot.title_register_number,
        }),
      }),
    };

    try {
      await saveDetails(payload);
    } catch (error) {
      const fieldErrors =
        error instanceof CaseCardDetailsValidationError
          ? Object.entries(error.errors).filter(([key]) => ERROR_FIELDS[key])
          : [];

      fieldErrors.forEach(([key, messages]) =>
        methods.setError(ERROR_FIELDS[key], { message: messages[0] })
      );

      if (fieldErrors.length === 0) {
        methods.setError('root', {
          message: i18n.t('Nie udało się zapisać, spróbuj ponownie.'),
        });
      }
    }
  };

  const focusProps = (name: string, onBlur: () => void) => ({
    onFocus: () => setFocused(name),
    onBlur: () => {
      setFocused('');
      onBlur();
    },
  });

  return createPortal(
    <div
      data-allow-scroll
      className="fixed left-0 top-0 z-[9999] flex h-[100dvh] w-full items-center justify-center overflow-y-auto bg-black/20 px-4 py-6 backdrop-blur-sm"
    >
      <div className="flex max-h-[calc(100dvh-48px)] w-full max-w-[343px] flex-col overflow-hidden rounded-[20px] bg-white shadow-xl md:max-w-[500px] md:rounded-[28px]">
        <div className="flex items-center justify-center gap-x-2 border-b border-dark-gray-100 px-6 pb-5 pt-6 text-blue-600 lg:px-8">
          <Pencil className="size-6 shrink-0" />
          <h3 className="text-center">{i18n.t('Uzupełnij swoje dane')}</h3>
        </div>
        <Form {...methods}>
          <form
            onSubmit={methods.handleSubmit(submit)}
            noValidate
            className="flex min-h-0 flex-1 flex-col"
          >
            {/* Only the fields scroll; the save button stays in view. */}
            <div className="flex min-h-0 flex-col gap-y-5 overflow-y-auto px-6 py-5">
              <ChatMessage
                variant="primary"
                className="items-start px-0 pb-0 md:px-0 lg:flex-col lg:px-0"
                consultant={
                  consultant && {
                    ...consultant,
                    jobDescription: i18n.t('Ekspert ds. odszkodowań'),
                  }
                }
                hideCloseButton
                description={`${greeting} ${request}`}
              />
              <FormField
                name="email"
                control={methods.control}
                rules={{ validate: validateEmail }}
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel className="text-dark-gray-500">
                      {i18n.t('Podaj swój adres e-mail')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        label={i18n.t('Adres e-mail')}
                        error={fieldState.error?.message}
                        success={
                          focused !== field.name &&
                          !fieldState.error &&
                          !!field.value
                        }
                        {...field}
                        {...focusProps(field.name, field.onBlur)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              {needsPlot && (
                // Long field labels must not wrap under the input text on
                // narrow phones.
                <div className="flex flex-col gap-y-3 [&_span.body]:right-3.5 [&_span.body]:truncate">
                  <p className="h5 text-dark-gray-500">
                    {i18n.t('Podaj dane działki')}
                  </p>
                  <FormField
                    name="plot.zip_code"
                    control={methods.control}
                    rules={{
                      validate: (value) =>
                        ZIP_CODE_PATTERN.test(value) ||
                        i18n.t('Wpisz kod pocztowy w formacie 00-000.'),
                    }}
                    render={({
                      field: { value, onChange, onBlur, ...field },
                      fieldState,
                    }) => (
                      <FormItem>
                        <FormControl>
                          <InputMask
                            mask="00-000"
                            inputMode="numeric"
                            autoComplete="postal-code"
                            label={i18n.t('Kod pocztowy działki')}
                            value={value}
                            error={fieldState.error?.message}
                            success={
                              focused !== field.name &&
                              !fieldState.error &&
                              !!value
                            }
                            onAccept={(next) => {
                              if (next === value) return;
                              onChange(next);
                              // The city list depends on the postal code.
                              methods.setValue('plot.city', '');
                              queryClient.setQueryData(['cities'], null);
                            }}
                            {...field}
                            {...focusProps(field.name, onBlur)}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <InputCity
                    name="plot.city"
                    prefix="plot"
                    controlled
                    focused={focused}
                    setFocused={setFocused}
                    disabled={!isZipCodeComplete}
                    displayValue={cityName}
                    resetAddressOnChange={false}
                  />
                  <FormField
                    name="plot.address"
                    control={methods.control}
                    rules={{
                      validate: (value) =>
                        !!value.trim() ||
                        i18n.t(
                          'Podaj ulicę i numer budynku albo numer działki.'
                        ),
                    }}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            maxLength={255}
                            label={i18n.t(
                              'Adres (ulica i numer) lub numer działki'
                            )}
                            error={fieldState.error?.message}
                            success={
                              focused !== field.name &&
                              !fieldState.error &&
                              !!field.value
                            }
                            {...field}
                            {...focusProps(field.name, field.onBlur)}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    name="plot.title_register_number"
                    control={methods.control}
                    rules={{
                      validate: (value) =>
                        !value ||
                        TITLE_REGISTER_NUMBER_PATTERN.test(value) ||
                        i18n.t('Wpisz numer KW w formacie KZ1W/00034512/8.'),
                    }}
                    render={({
                      field: { value, onChange, onBlur, ...field },
                      fieldState,
                    }) => (
                      <FormItem>
                        <FormControl>
                          <InputMask
                            mask="aa0a/00000000/0"
                            prepareChar={(char: string) => char.toUpperCase()}
                            autoComplete="off"
                            label={i18n.t(
                              'Numer księgi wieczystej (opcjonalnie)'
                            )}
                            value={value}
                            error={fieldState.error?.message}
                            success={
                              focused !== field.name &&
                              !fieldState.error &&
                              !!value
                            }
                            onAccept={(next) => {
                              if (next === value) return;
                              onChange(next);
                            }}
                            {...field}
                            {...focusProps(field.name, onBlur)}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <div className="flex items-center gap-1.5 rounded-md border border-dark-gray-200 bg-gray-200 px-2 py-2.5">
                    <IconInfo width={18} className="shrink-0 text-primary" />
                    <p className="font-jakarta text-xs font-medium leading-[18px] text-dark-gray-500">
                      {i18n.t(
                        'Format: 4 znaki kodu wydziału / 8 cyfr / cyfra kontrolna, np. KZ1W/00034512/8.'
                      )}
                    </p>
                  </div>
                </div>
              )}
              {methods.formState.errors.root?.message && (
                <ErrorMessage
                  errorMessage={methods.formState.errors.root.message}
                />
              )}
            </div>
            <div className="flex items-center justify-center border-t border-t-dark-gray-100 bg-gray-200 px-6 py-5 md:py-6 lg:px-8">
              <Button
                type="submit"
                size="small"
                loading={isPending}
                disabled={isPending || hasFieldErrors}
              >
                {i18n.t('Zapisz i przejdź dalej')}
                <ButtonLoading />
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>,
    document.body
  );
};

export default DetailsDialog;
